import { useState } from "react";
import { Link } from "react-router-dom";
import {
  Apple,
  Truck,
  Users,
  Building2,
  GraduationCap,
  DollarSign,
  CheckCircle,
  ArrowRight,
  ChevronDown,
  ChevronUp,
  Heart,
  Baby,
  Phone,
  Monitor,
  Smartphone,
  MapPin,
  Shield,
  Store,
  Briefcase,
  Clock,
  BookOpen,
  ChefHat,
  Warehouse,
  Scale,
  Leaf,
} from "lucide-react";

const FAQItem = ({ question, answer }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="border border-slate-200 bg-white">
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between p-5 text-left hover:bg-slate-50 transition-colors"
      >
        <span className="font-bold text-slate-900 pr-4">{question}</span>
        {open ? (
          <ChevronUp className="w-5 h-5 text-[#002B7F] flex-shrink-0" />
        ) : (
          <ChevronDown className="w-5 h-5 text-slate-400 flex-shrink-0" />
        )}
      </button>
      {open && (
        <div className="px-5 pb-5">
          <p className="text-slate-600 leading-relaxed">{answer}</p>
        </div>
      )}
    </div>
  );
};

const essentials = [
  "Fruits & vegetables",
  "Meat & poultry",
  "Dairy & eggs",
  "Grains & bread",
  "Baby food & formula",
  "Cleaning supplies",
  "Household basics",
];

const phases = [
  {
    phase: "Phase 1",
    title: "Pilot Launch",
    color: "bg-[#002B7F]",
    items: [
      "Pilot in Las Vegas and Reno low-income neighborhoods",
      "Limited essential product catalog",
      "Pickup hubs at schools, libraries, community centers",
    ],
  },
  {
    phase: "Phase 2",
    title: "Suburban Expansion",
    color: "bg-[#D4AF37]",
    textColor: "text-black",
    items: [
      "Expand to suburban areas with identified access gaps",
      "Add home delivery for seniors, disabled, and transportation-limited households",
      "Expand product catalog based on pilot data",
    ],
  },
  {
    phase: "Phase 3",
    title: "Rural Reach",
    color: "bg-emerald-600",
    items: [
      "Rural expansion using regional distribution hubs",
      "Partner with postal and logistics networks",
      "Mobile pickup points for remote communities",
    ],
  },
  {
    phase: "Phase 4",
    title: "Statewide Optimization",
    color: "bg-slate-800",
    items: [
      "Full statewide coverage after performance validation",
      "Continuous improvement based on community feedback",
      "Data-driven optimization of routes, pricing, and product selection",
    ],
  },
];

const fundingSources = [
  {
    icon: Building2,
    title: "State Budget Allocation",
    desc: "Targeted reallocation from existing programs toward direct food access",
  },
  {
    icon: Shield,
    title: "Federal Nutrition Grants",
    desc: "USDA food access programs and federal nutrition grants",
  },
  {
    icon: Users,
    title: "Public-Private Partnerships",
    desc: "Distribution and bulk purchasing agreements with suppliers",
  },
  {
    icon: Scale,
    title: "Social Impact Bonds",
    desc: "Tied to measurable outcomes: reduced food insecurity, improved child nutrition",
  },
];

const faqs = [
  {
    question: "Will this replace grocery stores?",
    answer: "No. This program focuses specifically on food deserts and affordability gaps — communities where grocery stores don't exist or are insufficient. It provides basic essentials, not full retail competition. It works alongside private grocery stores, not in place of them.",
  },
  {
    question: "Is this available to everyone or just low-income families?",
    answer: "The system is available to all Nevada residents, but it's designed specifically to address food deserts and affordability. It starts where need is greatest — low-income neighborhoods and underserved rural communities — and scales based on results. Optional sliding-scale contributions ensure sustainability.",
  },
  {
    question: "How is this different from existing food assistance?",
    answer: "Existing programs like SNAP and WIC help families afford food — but they don't solve the access problem. If there's no grocery store nearby, or if prices are too high, assistance dollars don't go far enough. This program creates the actual infrastructure for affordable, healthy food in communities that lack it.",
  },
  {
    question: "Will this actually lower grocery costs?",
    answer: "Yes. By purchasing essentials in bulk at state-negotiated prices and operating as a not-for-profit system, prices are significantly lower than retail. The focus on one primary option per essential item keeps costs minimal while maintaining quality.",
  },
  {
    question: "What about rural communities?",
    answer: "Rural expansion is built into Phase 3 of the rollout. Regional distribution hubs, partnerships with postal and logistics networks, and mobile pickup points ensure that rural communities have access. We will scale based on results, not assumptions.",
  },
  {
    question: "How do I know this is realistic?",
    answer: "The phased rollout is specifically designed for realism. We start with a controlled pilot, measure results, adjust, and then expand. Every phase is validated before the next begins. Funding comes from diverse, proven sources — not a single risky bet.",
  },
];

const FoodDesertsPage = () => {
  return (
    <div data-testid="food-deserts-page" className="pt-16 md:pt-20">
      {/* ── HERO ── */}
      <section className="relative py-16 md:py-24 bg-slate-900">
        <div className="absolute inset-0 bg-[#002B7F]/90" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <p className="text-sm font-bold uppercase tracking-[0.2em] text-[#D4AF37] mb-4">
            Ending Food Deserts and Food Swamps
          </p>
          <h1 className="text-4xl sm:text-5xl font-black tracking-tighter text-white mb-4 max-w-3xl">
            Ending Food Deserts and Food Swamps. Lowering Grocery Costs. Improving Child
            Nutrition.
          </h1>
          <p className="text-lg text-white/80 max-w-2xl mb-8">
            A Nevada plan to make healthy, affordable food accessible in every
            community — starting where it's needed most. Nevada families are
            paying too much for groceries, and too many communities lack access
            to healthy food. This plan creates a not-for-profit, state-supported
            grocery access system focused on basic essentials, nutrition, and
            affordability.
          </p>

          <div className="flex flex-wrap gap-4">
            <a
              href="#the-plan"
              className="inline-flex items-center gap-2 px-8 py-4 bg-[#D4AF37] hover:bg-[#B5952F] text-black font-bold uppercase tracking-widest text-sm transition-all hover:-translate-y-1 hover:shadow-xl"
            >
              Learn More
            </a>
            <Link
              to="/volunteer"
              className="inline-flex items-center gap-2 px-8 py-4 border-2 border-white text-white hover:bg-white hover:text-[#002B7F] font-bold uppercase tracking-widest text-sm transition-all"
            >
              Get Involved
            </Link>
          </div>

          {/* Video Overview */}
          <div className="mt-10 max-w-2xl">
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#D4AF37] mb-3">Watch the Overview</p>
            <div className="relative aspect-video bg-black border border-white/20 overflow-hidden">
              <iframe
                src="https://www.youtube.com/embed/6MS4Tc3Y2S0?rel=0&modestbranding=1"
                title="Ending Food Deserts and Food Swamps - Cooper for Nevada"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                className="absolute inset-0 w-full h-full"
                data-testid="food-deserts-video"
              />
            </div>
          </div>
        </div>
      </section>

      {/* ── THE PROBLEM ── */}
      <section className="py-16 md:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl md:text-3xl font-bold text-slate-900 mb-8">
            Nevada's Food Access Crisis
          </h2>

          <div className="grid lg:grid-cols-2 gap-12">
            <div>
              <p className="text-slate-600 leading-relaxed mb-6">
                Across Nevada, entire neighborhoods lack access to grocery stores
                that sell fresh, healthy food. These are food deserts — and
                they're growing. At the same time, many communities face "food
                swamps" where the only options are fast food, convenience stores,
                and ultra-processed products.
              </p>
              <p className="text-slate-600 leading-relaxed mb-8">
                High grocery prices, limited transportation, and geographic
                isolation mean that too many families — especially children,
                seniors, and people with disabilities — are unable to access the
                nutrition they need.
              </p>

              <div className="space-y-4">
                {[
                  {
                    icon: MapPin,
                    title: "Food Deserts",
                    desc: "Urban and rural communities with no nearby grocery stores selling fresh food",
                  },
                  {
                    icon: Store,
                    title: "Food Swamps",
                    desc: "Neighborhoods saturated with fast food and processed options — but no healthy alternatives",
                  },
                  {
                    icon: DollarSign,
                    title: "High Grocery Prices",
                    desc: "Families paying significantly more than necessary for basic essentials",
                  },
                  {
                    icon: Truck,
                    title: "Transportation Barriers",
                    desc: "Rural communities traveling long distances just to reach a grocery store",
                  },
                ].map((item) => {
                  const Icon = item.icon;
                  return (
                    <div
                      key={item.title}
                      className="flex items-start gap-3 p-4 bg-red-50 border border-red-200"
                    >
                      <Icon className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                      <div>
                        <p className="font-bold text-red-800 text-sm">{item.title}</p>
                        <p className="text-red-700 text-xs">{item.desc}</p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Impact cards */}
            <div className="space-y-4">
              {[
                {
                  icon: Baby,
                  title: "Children",
                  desc: "Kids relying on unhealthy, processed food because that's all their families can access. Growing bodies need real nutrition — and they're not getting it.",
                  bg: "bg-amber-50",
                  border: "border-amber-200",
                  iconColor: "text-amber-600",
                },
                {
                  icon: Heart,
                  title: "Seniors & Disabled Residents",
                  desc: "Elderly and disabled Nevadans struggling to shop — limited mobility, no transportation, and no delivery options in their communities.",
                  bg: "bg-blue-50",
                  border: "border-blue-200",
                  iconColor: "text-blue-600",
                },
                {
                  icon: MapPin,
                  title: "Rural Communities",
                  desc: "Families driving 30, 40, even 60+ miles just to reach a grocery store. That's not access — that's a barrier.",
                  bg: "bg-emerald-50",
                  border: "border-emerald-200",
                  iconColor: "text-emerald-600",
                },
              ].map((item) => {
                const Icon = item.icon;
                return (
                  <div
                    key={item.title}
                    className={`${item.bg} border ${item.border} p-6`}
                  >
                    <div className="flex items-center gap-3 mb-3">
                      <Icon className={`w-5 h-5 ${item.iconColor}`} />
                      <h3 className="font-bold text-slate-900">{item.title}</h3>
                    </div>
                    <p className="text-slate-600 text-sm leading-relaxed">
                      {item.desc}
                    </p>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* ── THE SOLUTION ── */}
      <section id="the-plan" className="py-16 md:py-24 bg-[#F8F9FA]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-2xl md:text-3xl font-bold text-slate-900 mb-4">
              A Nevada Essential Grocery Access System
            </h2>
            <p className="text-slate-600 max-w-2xl mx-auto">
              A state-supported, not-for-profit grocery access program focused
              exclusively on basic essentials — purchased in bulk, priced at
              cost, and delivered where it's needed most.
            </p>
          </div>

          <div className="grid lg:grid-cols-2 gap-8">
            {/* Essentials list */}
            <div className="bg-white border border-slate-200 p-8">
              <h3 className="text-lg font-bold text-slate-900 mb-4 flex items-center gap-2">
                <Apple className="w-5 h-5 text-emerald-600" />
                What's Included: Basic Essentials Only
              </h3>
              <div className="space-y-3 mb-6">
                {essentials.map((item) => (
                  <div key={item} className="flex items-center gap-3">
                    <CheckCircle className="w-4 h-4 text-emerald-500 flex-shrink-0" />
                    <span className="text-slate-700">{item}</span>
                  </div>
                ))}
              </div>
              <div className="bg-emerald-50 border border-emerald-200 p-4">
                <p className="text-emerald-800 text-sm font-medium">
                  One primary option per essential item when possible. Bulk
                  purchasing for lower cost. State-negotiated pricing with
                  suppliers.
                </p>
              </div>
            </div>

            {/* What it's NOT */}
            <div className="bg-white border border-slate-200 p-8">
              <h3 className="text-lg font-bold text-slate-900 mb-4 flex items-center gap-2">
                <Shield className="w-5 h-5 text-[#002B7F]" />
                What This Is NOT
              </h3>
              <div className="space-y-4">
                {[
                  {
                    title: "Not a full retail supermarket",
                    desc: "This is focused, essential grocery access — not a replacement for traditional stores",
                  },
                  {
                    title: "Not luxury goods",
                    desc: "No premium brands, specialty items, or excessive variety — just quality essentials at the lowest possible price",
                  },
                  {
                    title: "Not competing with local businesses",
                    desc: "Focused on food deserts and underserved communities where options don't currently exist",
                  },
                ].map((item) => (
                  <div key={item.title} className="border-b border-slate-200 pb-4 last:border-0 last:pb-0">
                    <p className="font-bold text-slate-900 text-sm mb-1">
                      {item.title}
                    </p>
                    <p className="text-slate-600 text-sm">{item.desc}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── HOW IT WORKS ── */}
      <section className="py-16 md:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-2xl md:text-3xl font-bold text-slate-900 mb-4">
              Simple, Accessible Grocery Ordering for Every Family
            </h2>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {/* Ordering */}
            <div>
              <h3 className="text-lg font-bold text-slate-900 mb-6">
                Ordering Options
              </h3>
              <div className="space-y-4">
                {[
                  { icon: Monitor, title: "Online Website", desc: "Simple, accessible ordering from any device" },
                  { icon: Smartphone, title: "Mobile App", desc: "Order from your phone anytime, anywhere" },
                  { icon: Phone, title: "Phone Ordering", desc: "Dedicated phone centers for those who prefer calling" },
                ].map((item) => {
                  const Icon = item.icon;
                  return (
                    <div key={item.title} className="flex items-start gap-4 p-4 bg-[#F8F9FA] border border-slate-200">
                      <div className="w-10 h-10 flex items-center justify-center bg-[#002B7F] text-white flex-shrink-0">
                        <Icon className="w-5 h-5" />
                      </div>
                      <div>
                        <p className="font-bold text-slate-900 text-sm">{item.title}</p>
                        <p className="text-slate-600 text-xs">{item.desc}</p>
                      </div>
                    </div>
                  );
                })}
              </div>

              <div className="mt-6 bg-[#002B7F]/10 border border-[#002B7F]/20 p-4">
                <p className="text-[#002B7F] text-sm font-medium">
                  Multilingual support, simple interface design, and
                  SNAP/WIC/HSA/FSA labeling throughout.
                </p>
              </div>
            </div>

            {/* Fulfillment */}
            <div>
              <h3 className="text-lg font-bold text-slate-900 mb-6">
                Fulfillment Options
              </h3>
              <div className="space-y-4">
                <div className="bg-[#F8F9FA] border border-slate-200 p-5">
                  <div className="flex items-center gap-3 mb-3">
                    <MapPin className="w-5 h-5 text-[#D4AF37]" />
                    <h4 className="font-bold text-slate-900 text-sm">
                      Pickup Locations
                    </h4>
                  </div>
                  <p className="text-slate-600 text-sm mb-3">
                    Convenient pickup at community locations families already
                    visit:
                  </p>
                  <div className="grid grid-cols-3 gap-2">
                    {["Schools", "Libraries", "Community Centers"].map(
                      (loc, i) => (
                        <div
                          key={i}
                          className="bg-white border border-slate-200 p-2 text-center text-xs font-medium text-slate-700"
                        >
                          {loc}
                        </div>
                      )
                    )}
                  </div>
                  <p className="text-slate-500 text-xs mt-2">
                    Weekend pickup model for maximum convenience
                  </p>
                </div>

                <div className="bg-[#F8F9FA] border border-slate-200 p-5">
                  <div className="flex items-center gap-3 mb-3">
                    <Truck className="w-5 h-5 text-[#D4AF37]" />
                    <h4 className="font-bold text-slate-900 text-sm">
                      Home Delivery
                    </h4>
                  </div>
                  <p className="text-slate-600 text-sm">
                    Available for seniors, disabled individuals, and households
                    without transportation. Nobody gets left behind because they
                    can't get to a pickup point.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── NUTRITION FOCUS ── */}
      <section className="py-16 md:py-24 bg-emerald-600">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-2xl md:text-3xl font-bold text-white mb-6">
                Improving Nutrition, Not Just Access
              </h2>
              <p className="text-white/80 leading-relaxed mb-6">
                Access only works when families also have the tools to use it.
                This plan goes beyond delivery — it includes education, recipes,
                and community-based support.
              </p>
              <div className="space-y-3">
                {[
                  "Aligned with real science-based nutrition guidelines",
                  "Focus on whole foods and balanced meals",
                  "Reducing dependence on ultra-processed foods",
                  "Recipes included with every grocery order",
                ].map((item, i) => (
                  <div key={item} className="flex items-start gap-2">
                    <Leaf className="w-5 h-5 text-emerald-200 flex-shrink-0 mt-0.5" />
                    <span className="text-white/90">{item}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="bg-white/10 border border-white/20 p-8">
              <div className="flex items-center gap-3 mb-4">
                <ChefHat className="w-6 h-6 text-white" />
                <h3 className="text-xl font-bold text-white">
                  Free Cooking & Nutrition Classes
                </h3>
              </div>
              <div className="space-y-3">
                {[
                  "Local chefs teaching in Nevada communities",
                  "Free cooking classes in low-income neighborhoods",
                  "Simple, affordable meal preparation training",
                  "Recipes included with grocery orders",
                  "Nutrition education for families and children",
                ].map((item) => (
                  <div key={item} className="flex items-start gap-2">
                    <CheckCircle className="w-4 h-4 text-emerald-300 flex-shrink-0 mt-0.5" />
                    <span className="text-white/90 text-sm">{item}</span>
                  </div>
                ))}
              </div>
              <p className="mt-6 text-emerald-200 font-bold text-sm">
                "Access only works when families also have the tools to use it."
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ── PHASED IMPLEMENTATION ── */}
      <section className="py-16 md:py-24 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-2xl md:text-3xl font-bold text-slate-900 mb-4">
              A Responsible, Phased Rollout
            </h2>
            <p className="text-slate-600">
              We will scale based on results, not assumptions.
            </p>
          </div>

          <div className="space-y-6">
            {phases.map((phase) => (
              <div key={phase.phase} className="grid md:grid-cols-[140px_1fr] gap-6">
                <div className={`${phase.color} p-4 flex items-center justify-center`}>
                  <p className={`${phase.textColor || "text-white"} font-black text-sm`}>
                    {phase.phase}
                  </p>
                </div>
                <div className="bg-[#F8F9FA] border border-slate-200 p-6">
                  <h3 className="font-bold text-slate-900 mb-3">
                    {phase.title}
                  </h3>
                  <ul className="space-y-2">
                    {phase.items.map((item) => (
                      <li key={item} className="flex items-start gap-2 text-sm">
                        <CheckCircle className="w-4 h-4 text-[#D4AF37] flex-shrink-0 mt-0.5" />
                        <span className="text-slate-600">{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── JOBS ── */}
      <section className="py-16 md:py-24 bg-[#002B7F]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-2xl md:text-3xl font-bold text-white mb-4">
              Creating Local Jobs While Lowering Costs
            </h2>
            <p className="text-white/70 max-w-2xl mx-auto">
              This program doesn't just deliver food — it creates economic
              opportunity in the communities that need it most.
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {[
              { icon: Warehouse, title: "Distribution Centers", desc: "Warehouses across Nevada — creating local infrastructure" },
              { icon: Users, title: "Priority Local Hiring", desc: "Jobs go to residents in the communities we serve" },
              { icon: Briefcase, title: "Good-Paying Jobs", desc: "Living wages with benefits — not gig work" },
              { icon: Truck, title: "Community Logistics", desc: "Delivery, pickup, and support roles across every region" },
            ].map((item) => {
              const Icon = item.icon;
              return (
                <div key={item.title} className="bg-white/10 border border-white/20 p-6">
                  <Icon className="w-6 h-6 text-[#D4AF37] mb-3" />
                  <h3 className="font-bold text-white text-sm mb-1">{item.title}</h3>
                  <p className="text-white/70 text-xs">{item.desc}</p>
                </div>
              );
            })}
          </div>

          <div className="mt-8 bg-white/10 border border-white/20 p-5 text-center max-w-2xl mx-auto">
            <p className="text-[#D4AF37] font-bold">
              Economic development in underserved areas — not just food access,
              but real opportunity.
            </p>
          </div>
        </div>
      </section>

      {/* ── FUNDING ── */}
      <section className="py-16 md:py-24 bg-[#F8F9FA]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-2xl md:text-3xl font-bold text-slate-900 mb-4">
              How This Program Is Funded
            </h2>
            <p className="text-slate-600 max-w-2xl mx-auto">
              Diverse, proven funding sources — not a single risky bet. Optional
              sliding-scale contributions for households who can afford it ensure
              long-term sustainability.
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {fundingSources.map((item) => {
              const Icon = item.icon;
              return (
                <div
                  key={item.title}
                  className="bg-white border border-slate-200 p-6 hover:border-[#D4AF37] transition-colors"
                >
                  <div className="w-10 h-10 flex items-center justify-center bg-[#002B7F] text-white mb-4">
                    <Icon className="w-5 h-5" />
                  </div>
                  <h3 className="font-bold text-slate-900 mb-2 text-sm">{item.title}</h3>
                  <p className="text-slate-600 text-xs">{item.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ── PRIVATE SECTOR SAFEGUARDS ── */}
      <section className="py-16 md:py-24 bg-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8">
            <Store className="w-10 h-10 text-[#002B7F] mx-auto mb-4" />
            <h2 className="text-2xl md:text-3xl font-bold text-slate-900 mb-4">
              Working Alongside, Not Replacing, Private Grocery Stores
            </h2>
          </div>
          <p className="text-slate-600 leading-relaxed mb-6 text-center">
            This program focuses on food deserts and affordability gaps — not
            competition with existing stores. It provides basic essentials in
            communities where options don't currently exist.
          </p>
          <div className="space-y-3">
            {[
              "Focuses on food deserts where no grocery store exists",
              "Basic essentials only — not full retail competition",
              "Designed as a public service for underserved communities",
              "Full compliance with state procurement and commerce laws",
              "Pilot-first structure reduces legal and operational risk",
            ].map((item, i) => (
              <div key={item} className="flex items-start gap-2 p-3 bg-[#F8F9FA] border border-slate-200">
                <CheckCircle className="w-4 h-4 text-[#D4AF37] flex-shrink-0 mt-0.5" />
                <span className="text-slate-700 text-sm">{item}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── WHY THIS MATTERS ── */}
      <section className="py-16 md:py-24 bg-[#002B7F]">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-2xl md:text-3xl font-bold text-white mb-8">
            Health, Dignity, and Opportunity
          </h2>

          <div className="grid sm:grid-cols-2 gap-4 text-left mb-8">
            {[
              "Better nutrition for children",
              "Lower long-term healthcare costs",
              "Reduced food insecurity",
              "Stronger, more stable communities",
              "Increased dignity for families",
              "Economic opportunity in underserved areas",
            ].map((item, i) => (
              <div key={item} className="flex items-center gap-3 bg-white/10 p-4">
                <CheckCircle className="w-5 h-5 text-[#D4AF37] flex-shrink-0" />
                <span className="text-white font-medium text-sm">{item}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── FAQ ── */}
      <section className="py-16 md:py-24 bg-[#F8F9FA]">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-2xl md:text-3xl font-bold text-slate-900 mb-4">
              Frequently Asked Questions
            </h2>
          </div>
          <div className="space-y-3">
            {faqs.map((faq) => (
              <FAQItem key={faq.question} question={faq.question} answer={faq.answer} />
            ))}
          </div>
        </div>
      </section>

      {/* ── CTA ── */}
      <section className="py-16 md:py-24 bg-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-2xl md:text-3xl font-bold text-slate-900 mb-6">
            A Nevada Where No One Goes Hungry
          </h2>
          <p className="text-slate-600 leading-relaxed mb-8">
            Nevada can be a state where every family has access to healthy,
            affordable food. Where children are not limited by where they live.
            And where no community is left behind.
          </p>
          <p className="text-[#002B7F] font-bold text-lg mb-8">
            For you. For your family. For Nevada.
          </p>
        </div>
      </section>

      <section className="py-16 md:py-24 bg-[#002B7F]">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-2xl md:text-3xl font-bold text-white mb-6">
            Healthy Food for Every Nevada Community
          </h2>
          <div className="flex flex-wrap justify-center gap-4">
            <Link
              to="/volunteer"
              className="inline-flex items-center gap-2 px-8 py-4 bg-[#D4AF37] hover:bg-[#B5952F] text-black font-bold uppercase tracking-widest text-sm transition-all hover:-translate-y-1 hover:shadow-xl"
            >
              Get Involved
            </Link>
            <Link
              to="/contact"
              className="inline-flex items-center gap-2 px-8 py-4 border-2 border-white text-white hover:bg-white hover:text-[#002B7F] font-bold uppercase tracking-widest text-sm transition-all"
            >
              Contact the Campaign
            </Link>
            <Link
              to="/policies/healthcare"
              className="inline-flex items-center gap-2 px-8 py-4 border-2 border-white/40 text-white/80 hover:border-white hover:text-white font-bold uppercase tracking-widest text-sm transition-all"
            >
              eNVy Healthcare <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default FoodDesertsPage;
